import type {
  InductionMethod,
  PickupDropOffSavePayload,
} from '../../service-step/sections/pickup-drop-off/pickup-drop-off.state';
import { formatHhmmAsDisplayTime } from '../../service-step/sections/pickup-drop-off/mappers/map-pickup-availability';

/**
 * Salesforce pickup-location codes with review-card i18n labels.
 * Aligned with pickup-service `pickup-from-fallback.json`.
 */
const PICKUP_LOCATION_CODES = [
  'BD',
  'BA',
  'BE',
  'CO',
  'DE',
  'FR',
  'FD',
  'FP',
  'GA',
  'GH',
  'KI',
  'LA',
  'LD',
  'LO',
  'MR',
  'MA',
  'OF',
  'OD',
  'PD',
  'PH',
  'PS',
  'RE',
  'RP',
  'SE',
  'SC',
  'SH',
  'SD',
  'SW',
  'VA',
  'WA',
] as const;

/** Display model for the induction (drop-off / pickup / label-only) review card. */
export interface ReviewInductionView {
  method: InductionMethod;
  titleI18nKey: string;
  pickupDateLabel: string | null;
  pickupTimeLabel: string | null;
  locationCode: string | null;
  /** API pickup-from label persisted at save time (preferred over i18n / raw code). */
  locationLabel: string | null;
  locationI18nKey: string | null;
  /** Courier pickup confirmation id, shown after Location when a pickup was scheduled (OM-731). */
  pickupConfirmationId: string | null;
  /** Optional courier instructions from the Service step (OM-830). */
  courierInstructions: string | null;
}

const TITLE_I18N_KEYS: Record<InductionMethod, string> = {
  'drop-off': 'createShipment.reviewAndPay.induction.dropOffTitle',
  pickup: 'createShipment.reviewAndPay.induction.pickupTitle',
  'label-only': 'createShipment.reviewAndPay.induction.labelOnlyTitle',
};

/**
 * Formats a YYYY-MM-DD pickup date as `August 25, 2026` (en) or the French-Canada equivalent.
 */
export function formatPickupDate(isoDate: string, locale: 'en' | 'fr'): string {
  const normalized = isoDate.replaceAll('-', '/');
  const [year, month, day] = normalized.split('/').map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) {
    return isoDate;
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Normalizes a pickup clock value to HHmm when the backend sent `HH:mm`.
 *
 * @param value Raw pickup ready/close time (`0900`, `09:00`, or a non-clock label)
 */
function normalizePickupClock(value: string): string {
  const trimmed = value.trim();
  const colonMatch = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (!colonMatch) {
    return trimmed;
  }

  return `${colonMatch[1].padStart(2, '0')}${colonMatch[2]}`;
}

/**
 * Formats one HHmm value as a 12-hour clock label (`12:00 PM`) for EN and FR (OM-679).
 * `HH:mm` from Shipment Lambda is normalized first. Other non-HHmm input is returned
 * trimmed; blank input yields null.
 *
 * @param hhmm Pickup clock value
 * @param locale Active UI locale (passed through to the shared display helper)
 */
export function formatPickupClock(hhmm: string, locale: 'en' | 'fr' = 'en'): string | null {
  const normalized = normalizePickupClock(hhmm);
  if (!normalized) {
    return null;
  }

  if (!/^\d{4}$/.test(normalized)) {
    return normalized;
  }

  return formatHhmmAsDisplayTime(normalized, locale);
}

/**
 * Formats a pickup window as a 12-hour range (`12:00 PM – 5:00 PM`) for EN and FR (OM-679).
 */
export function formatPickupTimeRange(
  windowFrom: string | undefined,
  windowTo: string | undefined,
  locale: 'en' | 'fr' = 'en',
): string | null {
  const from = windowFrom ? formatPickupClock(windowFrom, locale) : null;
  const to = windowTo ? formatPickupClock(windowTo, locale) : null;
  if (!from || !to) {
    return null;
  }

  return `${from} – ${to}`;
}

/**
 * Maps persisted pickup/drop-off state into the induction review card.
 * Ignores `pickupDraft` unless the active method is pickup.
 */
export function mapReviewInduction(
  payload: PickupDropOffSavePayload | undefined,
  locale: 'en' | 'fr' = 'en',
  pickupConfirmationId?: string,
): ReviewInductionView {
  const method: InductionMethod = payload?.method ?? 'drop-off';
  const isPickup = method === 'pickup';
  const locationCode = isPickup ? (payload?.locationCode ?? null) : null;
  const locationLabel = isPickup ? payload?.locationLabel?.trim() || null : null;
  const knownLocation =
    !locationLabel &&
    locationCode &&
    PICKUP_LOCATION_CODES.includes(locationCode as (typeof PICKUP_LOCATION_CODES)[number]);

  const courierInstructions =
    isPickup && payload?.courierInstructions?.trim() ? payload.courierInstructions.trim() : null;

  return {
    method,
    titleI18nKey: TITLE_I18N_KEYS[method],
    pickupDateLabel:
      isPickup && payload?.selectedDate ? formatPickupDate(payload.selectedDate, locale) : null,
    pickupTimeLabel: isPickup
      ? formatPickupTimeRange(payload?.windowFrom, payload?.windowTo, locale)
      : null,
    locationCode,
    locationLabel,
    locationI18nKey: knownLocation
      ? `createShipment.reviewAndPay.induction.locations.${locationCode}`
      : null,
    pickupConfirmationId: isPickup ? pickupConfirmationId?.trim() || null : null,
    courierInstructions,
  };
}
